/**
 * Timeline & Absence Notes Endpoints
 *
 * Injected into `attendanceApi` from `attendanceApiBase.ts`.
 * Covers: Timeline queries, Absence Notes CRUD, Daily Log, Lateness review,
 *         Data Sources, Conflict detection, Issue Resolutions.
 *
 * @module attendance/api/timelineEndpoints
 */

import { attendanceApi } from "./attendanceApiBase";
import { supabase } from "../../../lib/supabase";
import {
  fetchStudentDayTimeline,
  fetchTimelineLessons,
  fetchAbsenceNotesForDate,
  fetchDailyLog,
  fetchAttendanceSummaryForDate,
  fetchDataSourcesForDate,
  fetchLessonNotes,
  checkAbsenceConflicts,
  softDeleteAbsenceNote,
  mergeAbsenceNotes,
  createNoteFromDailyLog,
  linkDailyLogToNote,
  updateAbsenceNote,
  setNoteStatus,
  updateNoteAction,
  reviewLatenessPerLesson,
  clearLatenessPerLesson,
  getOrCreateAbsenceNote,
  type TimelineEntry,
  type TimelineLesson,
  type ServiceResult,
  type MergeAbsenceNotesParams,
  type UpdateNoteActionParams,
  type ReviewLatenessParams,
  type GetOrCreateNoteParams,
  type DataSourcesResponse,
} from "../../absence-tracking/shared/api";

// ============================================================================
// Injected Endpoints
// ============================================================================

const timelineEndpointsApi = attendanceApi.injectEndpoints({
  endpoints: (builder) => ({
    // ========================================================================
    // Timeline Queries
    // ========================================================================

    getStudentDayTimeline: builder.query<
      TimelineEntry[],
      { studentId: string; date: string }
    >({
      async queryFn({ studentId, date }) {
        try {
          if (!studentId || !date) return { data: [] };
          const data = await fetchStudentDayTimeline({ studentId, date });
          return { data };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      providesTags: (_r, _e, { studentId, date }) => [
        { type: "Timeline", id: `${studentId}-${date}` },
      ],
      keepUnusedDataFor: 60,
    }),

    getTimelineLessons: builder.query<
      TimelineLesson[],
      { studentId: string; date: string }
    >({
      async queryFn({ studentId, date }) {
        try {
          if (!studentId || !date) return { data: [] };
          const data = await fetchTimelineLessons(studentId, date);
          return { data };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      providesTags: (_r, _e, { studentId, date }) => [
        { type: "TimelineLessons", id: `${studentId}-${date}` },
      ],
      keepUnusedDataFor: 60,
    }),

    getAbsenceNotesForDate: builder.query<
      Record<string, unknown>[],
      { studentId: string; date: string }
    >({
      async queryFn({ studentId, date }) {
        try {
          if (!studentId || !date) return { data: [] };
          const data = await fetchAbsenceNotesForDate(studentId, date);
          return { data };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      providesTags: (_r, _e, { studentId, date }) => [
        { type: "AbsenceNotes", id: `${studentId}-${date}` },
      ],
      keepUnusedDataFor: 30,
    }),

    getDailyLog: builder.query<
      Record<string, unknown> | null,
      { studentId: string; date: string }
    >({
      async queryFn({ studentId, date }) {
        try {
          if (!studentId || !date) return { data: null };
          const data = await fetchDailyLog(studentId, date);
          return { data };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      providesTags: (_r, _e, { studentId, date }) => [
        { type: "DailyLog", id: `${studentId}-${date}` },
      ],
      keepUnusedDataFor: 30,
    }),

    getAttendanceSummaryForDate: builder.query<
      Record<string, unknown>[],
      { studentId: string; date: string }
    >({
      async queryFn({ studentId, date }) {
        try {
          if (!studentId || !date) return { data: [] };
          const data = await fetchAttendanceSummaryForDate(studentId, date);
          return { data };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      providesTags: (_r, _e, { studentId, date }) => [
        { type: "AttendanceSummary", id: `${studentId}-${date}` },
      ],
      keepUnusedDataFor: 30,
    }),

    getDataSourcesForDate: builder.query<
      DataSourcesResponse,
      { studentId: string; date: string }
    >({
      async queryFn({ studentId, date }) {
        try {
          if (!studentId || !date) {
            return {
              data: {
                absence_notes: [],
                daily_log: null,
                attendance_summary: null,
              },
            };
          }
          const data = await fetchDataSourcesForDate(studentId, date);
          return { data };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      providesTags: (_r, _e, { studentId, date }) => [
        { type: "AbsenceNotes", id: `${studentId}-${date}` },
        { type: "DailyLog", id: `${studentId}-${date}` },
        { type: "AttendanceSummary", id: `${studentId}-${date}` },
      ],
      keepUnusedDataFor: 30,
    }),

    getLessonNotes: builder.query<
      Record<string, string>,
      { studentId: string; lessonIds: string[] }
    >({
      async queryFn({ studentId, lessonIds }) {
        try {
          if (!studentId || !lessonIds.length) return { data: {} };
          const data = await fetchLessonNotes(studentId, lessonIds);
          return { data };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      providesTags: ["LessonNotes"],
      keepUnusedDataFor: 60,
    }),

    checkAbsenceConflicts: builder.query<
      Array<{
        conflictDate: string;
        presenceStatus: string;
        checkInTime: string | null;
        checkOutTime: string | null;
        hasLessons: boolean;
        lessonCount: number;
      }>,
      { studentId: string; startDate: string; endDate?: string | null }
    >({
      async queryFn({ studentId, startDate, endDate }) {
        try {
          if (!studentId || !startDate) return { data: [] };
          const conflicts = await checkAbsenceConflicts({
            studentId,
            startDate,
            endDate,
          });
          return { data: conflicts };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      providesTags: (_r, _e, { studentId }) => [
        { type: "AbsenceConflicts", id: studentId },
      ],
      keepUnusedDataFor: 30,
    }),

    // ========================================================================
    // Absence Note Mutations
    // ========================================================================

    softDeleteAbsenceNote: builder.mutation<ServiceResult, string>({
      async queryFn(noteId) {
        try {
          const result = await softDeleteAbsenceNote(noteId);
          if (!result.success) {
            return {
              error: {
                status: "CUSTOM_ERROR",
                error: result.error || "Failed to delete note",
              },
            };
          }
          return { data: result };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      invalidatesTags: [
        "AbsenceNotes",
        "Timeline",
        "TimelineLessons",
        "Fehlzeiten",
      ],
    }),

    mergeAbsenceNotes: builder.mutation<ServiceResult, MergeAbsenceNotesParams>(
      {
        async queryFn(params) {
          try {
            const result = await mergeAbsenceNotes(params);
            if (!result.success) {
              return {
                error: {
                  status: "CUSTOM_ERROR",
                  error: result.error || "Failed to merge notes",
                },
              };
            }
            return { data: result };
          } catch (error) {
            return { error: { status: "CUSTOM_ERROR", error: String(error) } };
          }
        },
        invalidatesTags: ["AbsenceNotes", "Fehlzeiten"],
      },
    ),

    createNoteFromDailyLog: builder.mutation<
      ServiceResult<{ noteId: string }>,
      {
        studentId: string;
        schoolId: string;
        date: string;
        presenceStatus: string;
        notes?: string;
        createdBy?: string;
      }
    >({
      async queryFn(params) {
        try {
          const result = await createNoteFromDailyLog(params);
          if (!result.success) {
            return {
              error: {
                status: "CUSTOM_ERROR",
                error: result.error || "Failed to create note",
              },
            };
          }
          return { data: result };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      invalidatesTags: ["AbsenceNotes", "DailyLog", "Fehlzeiten"],
    }),

    linkDailyLogToNote: builder.mutation<
      ServiceResult,
      { studentId: string; date: string; noteId: string }
    >({
      async queryFn({ studentId, date, noteId }) {
        try {
          const result = await linkDailyLogToNote(studentId, date, noteId);
          if (!result.success) {
            return {
              error: {
                status: "CUSTOM_ERROR",
                error: result.error || "Failed to link daily log",
              },
            };
          }
          return { data: result };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      invalidatesTags: ["DailyLog"],
    }),

    updateAbsenceNote: builder.mutation<
      ServiceResult,
      {
        noteId: string;
        updates: {
          startTime?: string;
          endTime?: string;
          reason?: string;
          absenceStatus?: string;
        };
      }
    >({
      async queryFn({ noteId, updates }) {
        try {
          const result = await updateAbsenceNote(noteId, updates);
          if (!result.success) {
            return {
              error: {
                status: "CUSTOM_ERROR",
                error: result.error || "Failed to update note",
              },
            };
          }
          return { data: result };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      invalidatesTags: ["AbsenceNotes", "Fehlzeiten"],
    }),

    setNoteStatus: builder.mutation<
      ServiceResult,
      { noteId: string; status: string; byId?: string }
    >({
      async queryFn({ noteId, status, byId }) {
        try {
          const result = await setNoteStatus(noteId, status, byId);
          if (!result.success) {
            return {
              error: {
                status: "CUSTOM_ERROR",
                error: result.error || "Failed to set status",
              },
            };
          }
          return { data: result };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      invalidatesTags: ["AbsenceNotes", "Fehlzeiten"],
    }),

    updateNoteAction: builder.mutation<ServiceResult, UpdateNoteActionParams>({
      async queryFn(params) {
        try {
          const result = await updateNoteAction(params);
          if (!result.success) {
            return {
              error: {
                status: "CUSTOM_ERROR",
                error: result.error || "Failed to update action",
              },
            };
          }
          return { data: result };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      invalidatesTags: ["AbsenceNotes", "Fehlzeiten"],
    }),

    reviewLateness: builder.mutation<ServiceResult, ReviewLatenessParams>({
      async queryFn(params) {
        try {
          const result = await reviewLatenessPerLesson(params);
          if (!result.success) {
            return {
              error: {
                status: "CUSTOM_ERROR",
                error: result.error || "Failed to review lateness",
              },
            };
          }
          return { data: result };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      invalidatesTags: ["Timeline", "TimelineLessons", "Fehlzeiten"],
    }),

    clearLateness: builder.mutation<
      ServiceResult,
      { studentId: string; lessonId: string }
    >({
      async queryFn({ studentId, lessonId }) {
        try {
          const result = await clearLatenessPerLesson(studentId, lessonId);
          if (!result.success) {
            return {
              error: {
                status: "CUSTOM_ERROR",
                error: result.error || "Failed to clear lateness",
              },
            };
          }
          return { data: result };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      invalidatesTags: ["Timeline", "TimelineLessons", "Fehlzeiten"],
    }),

    getOrCreateAbsenceNote: builder.mutation<
      ServiceResult<{ noteId: string }>,
      GetOrCreateNoteParams
    >({
      async queryFn(params) {
        try {
          const result = await getOrCreateAbsenceNote(params);
          if (!result.success) {
            return {
              error: {
                status: "CUSTOM_ERROR",
                error: result.error || "Failed to get/create note",
              },
            };
          }
          return { data: result };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      invalidatesTags: ["AbsenceNotes", "Fehlzeiten"],
    }),

    // ========================================================================
    // Issue Resolutions
    // ========================================================================

    getIssueResolutionsForAbsence: builder.query<
      Array<{
        id: string;
        action: string;
        note: string | null;
        resolved_at: string;
        resolver_name: string;
        lesson_id: string | null;
      }>,
      { studentId: string; dateIso: string }
    >({
      async queryFn({ studentId, dateIso }) {
        try {
          const { data, error } = await supabase.rpc(
            "ops_get_issue_resolutions_for_date_react",
            {
              p_student_id: studentId,
              p_date: dateIso,
            },
          );

          if (error) {
            return {
              error: { status: "CUSTOM_ERROR", error: String(error.message) },
            };
          }

          return {
            data: (data || []).map((r: any) => ({
              id: r.id,
              action: r.action,
              note: r.note,
              resolved_at: r.resolved_at,
              resolver_name: r.resolver_name || "Unbekannt",
              lesson_id: r.lesson_id,
            })),
          };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      providesTags: (_result, _error, { studentId, dateIso }) => [
        { type: "IssueResolutions", id: `${studentId}_${dateIso}` },
      ],
    }),
  }),
  overrideExisting: false,
});

// ============================================================================
// Generated Hooks
// ============================================================================

export const {
  // Timeline queries
  useGetStudentDayTimelineQuery,
  useLazyGetStudentDayTimelineQuery,
  useGetTimelineLessonsQuery,
  useLazyGetTimelineLessonsQuery,
  useGetAbsenceNotesForDateQuery,
  useLazyGetAbsenceNotesForDateQuery,
  useGetDailyLogQuery,
  useLazyGetDailyLogQuery,
  useGetAttendanceSummaryForDateQuery,
  useLazyGetAttendanceSummaryForDateQuery,
  useGetDataSourcesForDateQuery,
  useLazyGetDataSourcesForDateQuery,
  useGetLessonNotesQuery,
  useLazyGetLessonNotesQuery,
  useCheckAbsenceConflictsQuery,
  useLazyCheckAbsenceConflictsQuery,
  // Absence note mutations
  useSoftDeleteAbsenceNoteMutation,
  useMergeAbsenceNotesMutation,
  useCreateNoteFromDailyLogMutation,
  useLinkDailyLogToNoteMutation,
  useUpdateAbsenceNoteMutation,
  useSetNoteStatusMutation,
  useUpdateNoteActionMutation,
  useReviewLatenessMutation,
  useClearLatenessMutation,
  useGetOrCreateAbsenceNoteMutation,
  // Issue resolutions
  useGetIssueResolutionsForAbsenceQuery,
} = timelineEndpointsApi;
